import * as React from 'react';
import { actions } from 'react-redux-form';
import * as Fuse from 'fuse.js';
import * as shortid from 'shortid';
import { debounce, isEqual } from 'lodash';
import { Link } from 'react-router-dom';
import { DateTime, Form, Notification, Box } from '../';
import fields from '../../data/fields';
import search from '../../data/search';
import conditions from '../../data/conditions';
import { ButtonGroup } from '../../components';

interface Props {
  items: Array<any>;
  showEmail?: boolean;
  showJoinDate?: boolean;
  handleClick?: any;
  hideSearch?: boolean;
  renderBody?: any;
  noAnimation?: boolean;
  showVehicleFilter?: boolean;
  vehicleTypes?: Array<any>;
}

interface State {
  resetForm: Function;
  items: Array<any>;
  allItems: Array<any>;
  search: any;
  fields: any;
  path: any;
  name: string;
  key: string;
}

class BaseList<T extends Props = Props> extends React.Component<T, State> {
  static fields = fields;
  static search = search;

  static defaultProps = {
    handleClick: false,
    showJoinDate: true,
    hideSearch: false,
    showVehicleFilter: false,
  };

  constructor(props, options) {
    super(props);
    this.state = {
      resetForm: () => {},
      items: this.props.items,
      allItems: this.props.items,
      search: options.search,
      fields: options.fields,
      path: options.path,
      name: options.name,
      key: shortid.generate(),
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.handleDispatch = this.handleDispatch.bind(this);
    this.renderHead = this.renderHead.bind(this);
    this.renderBody = this.renderBody.bind(this);
    this.renderFoot = this.renderFoot.bind(this);
    this.renderLeft = this.renderLeft.bind(this);
  }

  componentDidMount() {
    this.handleSearch = debounce(this.handleSearch, 200);
    this.handleFilter = debounce(this.handleFilter, 200);
  }

  componentDidUpdate() {
    if (!isEqual(this.state.allItems, this.props.items)) {
      this.setState({
        // eslint-disable-line
        allItems: this.props.items,
      });
      this.state.resetForm();
      this.handleSearch({ search: '' });
    }
  }

  handleDispatch(dispatch) {
    this.setState({
      resetForm: () => dispatch(actions.change('search.search', '')),
    });
  }

  handleSearch(data) {
    this.setState({ key: shortid.generate() });
    if (data.search) {
      const fuse = new Fuse(this.props.items, this.state.search);
      this.setState({
        items: fuse.search(data.search),
      });
    } else {
      this.setState({
        items: this.props.items,
      });
    }
  }

  // This will only be used on the vehicle List
  handleFilter(data) {
    if (data.filter !== 'Filter...') {
      if (
        Number(data.filter) === 0 ||
        Number(data.filter) === 1 ||
        Number(data.filter) === 2
      ) {
        this.setState({
          items: this.props.items.filter(
            (item) => item.condition === Number(data.filter),
          ),
        });
      } else {
        const type =
          Number(data.filter) === -1
            ? 1
            : Number(data.filter) === -2
            ? 2
            : Number(data.filter);

        this.setState({
          items: this.props.items.filter(
            (item) => item.vehicle_type_id === type,
          ),
        });
      }
    } else {
      this.setState({
        items: this.props.items,
      });
    }
  }

  renderLeft(item: any, index: number) {
    return null;
  }

  renderHead(item: any, index: number) {
    return <strong>{item.name}</strong>;
  }

  renderBody(item: any, index: number) {
    if (this.props.renderBody !== undefined) {
      return this.props.renderBody(item);
    }
    return null;
  }

  renderFoot(item: any, index: number) {
    if (this.props.showJoinDate) {
      return (
        <small>
          <br />
          Created <DateTime date={item.created_at} />
        </small>
      );
    }
  }

  render() {
    const renderContents = (item, index) => (
      <>
        <figure className="media-left">{this.renderLeft(item, index)}</figure>
        <div className="media-content">
          {this.renderHead(item, index)}
          {this.renderBody(item, index)}
          {this.renderFoot(item, index)}
        </div>
      </>
    );
    return (
      <>
        {!this.props.hideSearch && !this.props.showVehicleFilter && (
          <div className="field">
            <Form
              type="search"
              fields={this.state.fields}
              hasButton={false}
              handleChange={this.handleSearch}
              getDispatch={this.handleDispatch}
            />
          </div>
        )}
        {this.props.showVehicleFilter && (
          <ButtonGroup className="button-group field is-grouped sb">
            <div className="field">
              <Form
                type="search"
                fields={this.state.fields}
                hasButton={false}
                handleChange={this.handleSearch}
                getDispatch={this.handleDispatch}
              />
            </div>
            <div className="field">
              <Form
                key="filter"
                type="form"
                fields={[
                  {
                    name: 'filter',
                    type: 'select',
                    placeholder: 'Filter...',
                  },
                ]}
                values={{
                  filter: conditions.concat(
                    this.props.vehicleTypes.map((vehicleType, index) => ({
                      key: index.toString(),
                      name: vehicleType.name,
                      value:
                        vehicleType.id === 1
                          ? -1
                          : vehicleType.id === 2
                          ? -2
                          : vehicleType.id,
                      class: '',
                    })),
                  ),
                }}
                hasButton={false}
                handleChange={this.handleFilter}
                getDispatch={this.handleDispatch}
              />
            </div>
          </ButtonGroup>
        )}
        {this.props.items.length === 0 ? (
          <Notification type="default">No {this.state.name} found</Notification>
        ) : this.state.items.length === 0 ? (
          <Notification type="default">
            No relevant {this.state.name} found
          </Notification>
        ) : (
          <Box>
            {this.state.items.map((item, index) =>
              this.props.handleClick ? (
                <a
                  className="media has-text-dark"
                  key={this.state.key + index}
                  onClick={() => this.props.handleClick(item, index)}
                >
                  {renderContents(item, index)}
                </a>
              ) : !!this.state.path ? (
                <Link
                  className="media has-text-dark"
                  key={this.state.key + index}
                  to={`/${this.state.path}/${item.id}`}
                >
                  {renderContents(item, index)}
                </Link>
              ) : (
                <div className="media" key={this.state.key + index}>
                  {renderContents(item, index)}
                </div>
              ),
            )}
          </Box>
        )}
      </>
    );
  }
}

export default BaseList;
